(() => {
  'use strict';

  const INDEX_SECTION = 'ace-collection-search-index';
  const MAX_PRODUCTS = 25000;
  const normalize = (value) => String(value || '').normalize('NFKC').replace(/[\p{Pd}−]/gu, '-').replace(/\s+/g, ' ').trim().toUpperCase();
  const compact = (value) => normalize(value).replace(/[-_\s]/g, '');
  const scopeURL = (href) => {
    const url = new URL(href);
    ['page', 'section_id', 'sections'].forEach((key) => url.searchParams.delete(key));
    url.hash = '';
    url.searchParams.sort();
    return url;
  };
  const matches = (product, query) => {
    const terms = normalize(query).split(' ').filter(Boolean);
    return terms.length > 0 && terms.every((term) => product.title.includes(term) || product.skus.some((sku) => sku.includes(term) || (compact(term) && compact(sku).includes(compact(term)))));
  };
  const abortError = () => new DOMException('Aborted', 'AbortError');
  const mapConcurrent = async (items, operation, concurrency = 3) => {
    const output = new Array(items.length);
    let next = 0;
    await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, async () => {
      while (next < items.length) {
        const index = next++;
        output[index] = await operation(items[index], index);
      }
    }));
    return output;
  };

  class AceCollectionSearch extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this.initialize());
    }

    initialize() {
      if (this.initialized || !this.isConnected) return;
      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');
      this.status = this.querySelector('[role="status"]');
      this.clearButton = this.querySelector('[data-ace-search-clear]');
      this.root = this.closest('collection-component');
      this.container = this.root?.querySelector('#ProductGridContainer');
      if (!this.input || !this.form || !this.root || !this.container) return;
      this.initialized = true;
      this.generation = 0;
      this.pageSize = Number(this.dataset.pageSize) || 16;
      this.listeners = new AbortController();
      this.cards = new Map();
      this.index = null;
      this.indexPromise = null;
      this.currentScope = scopeURL(window.location.href).href;
      this.captureOriginal();
      this.observer = new MutationObserver(() => this.nativeGridChanged());
      this.observeGrid();
      const options = { signal: this.listeners.signal };
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        clearTimeout(this.debounce);
        this.search(1);
      }, options);
      this.input.addEventListener('input', (event) => {
        clearTimeout(this.debounce);
        this.cancelResults();
        this.clearButton.hidden = !this.input.value;
        if (!this.input.value.trim()) return this.clear();
        if (!event.isComposing) this.debounce = setTimeout(() => this.search(1), 350);
      }, options);
      this.input.addEventListener('compositionend', () => {
        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => this.search(1), 350);
      }, options);
      this.clearButton.addEventListener('click', () => {
        this.clear();
        this.input.focus();
      }, options);
      this.root.addEventListener('input', (event) => {
        if (event.target.closest('facet-filters-form')) this.facetsChanging();
      }, { ...options, capture: true });
      this.root.addEventListener('click', (event) => {
        if (event.target.closest('facet-remove, .js-facet-remove')) this.facetsChanging();
        const button = event.target.closest('[data-ace-search-page]');
        if (button && this.container.contains(button)) this.search(Number(button.dataset.aceSearchPage), true);
      }, { ...options, capture: true });
      window.addEventListener('popstate', () => {
        if (scopeURL(window.location.href).href !== this.currentScope) this.facetsChanging();
      }, options);
      this.hidden = false;
    }

    disconnectedCallback() {
      this.cancelResults();
      this.indexController?.abort();
      this.listeners?.abort();
      this.observer?.disconnect();
      clearTimeout(this.debounce);
      clearTimeout(this.facetTimeout);
      this.initialized = false;
    }

    observeGrid() {
      this.observer?.observe(this.container, { childList: true });
    }

    captureOriginal() {
      this.originalHTML = this.container.innerHTML;
      const state = this.container.querySelector('[data-ace-search-state]');
      this.nativePage = Number(state?.dataset.page) || 1;
      this.nativeCount = Number(state?.dataset.count);
      this.cards = new Map();
      this.cacheCards(this.container, this.nativePage);
    }

    cacheCards(source, page) {
      const cards = new Map();
      source.querySelectorAll('#product-grid > [data-ace-product-id]').forEach((card) => {
        cards.set(card.dataset.aceProductId, card.outerHTML);
      });
      this.cards.set(page, cards);
      return cards;
    }

    restoreOriginal() {
      if (!this.originalHTML || !this.container) return;
      this.observer?.disconnect();
      this.container.innerHTML = this.originalHTML;
      this.observeGrid();
      this.setBusy(false);
    }

    cancelResults() {
      this.generation = (this.generation || 0) + 1;
      this.resultController?.abort();
    }

    resetIndex() {
      this.indexController?.abort();
      this.indexController = null;
      this.indexPromise = null;
      this.index = null;
    }

    clear() {
      clearTimeout(this.debounce);
      this.cancelResults();
      if (!this.index) this.resetIndex();
      this.input.value = '';
      this.clearButton.hidden = true;
      if (!this.waitingForFacets) this.restoreOriginal();
      this.message('');
    }

    facetsChanging() {
      clearTimeout(this.debounce);
      clearTimeout(this.facetTimeout);
      this.cancelResults();
      this.resetIndex();
      if (!this.waitingForFacets) this.restoreOriginal();
      this.waitingForFacets = true;
      this.querySelector('[type="submit"]').disabled = true;
      if (this.input.value.trim()) this.message(this.dataset.loadingMessage);
      this.facetTimeout = setTimeout(() => {
        if (!this.waitingForFacets) return;
        this.setBusy(false);
        this.message(this.dataset.filterErrorMessage, true);
        const reload = document.createElement('a');
        reload.href = window.location.href;
        reload.textContent = this.dataset.reloadLabel;
        reload.className = 'link';
        this.status.append(' ', reload);
      }, 25000);
    }

    nativeGridChanged() {
      this.cancelResults();
      this.resetIndex();
      clearTimeout(this.facetTimeout);
      this.waitingForFacets = false;
      this.currentScope = scopeURL(window.location.href).href;
      this.captureOriginal();
      this.querySelector('[type="submit"]').disabled = false;
      this.setBusy(false);
      if (this.input.value.trim()) this.search(1);
      else this.message('');
    }

    message(text, error = false) {
      this.status.textContent = text;
      this.status.toggleAttribute('data-error', error);
    }

    setBusy(busy) {
      this.container.classList.toggle('ace-search-loading', busy);
      this.container.setAttribute('aria-busy', String(busy));
    }

    isCurrent(generation, scope) {
      return this.isConnected && generation === this.generation && !this.waitingForFacets && scopeURL(window.location.href).href === scope;
    }

    async fetchSection(scope, section, page, signal) {
      if (signal.aborted) throw abortError();
      const url = new URL(scope);
      url.searchParams.set('section_id', section);
      url.searchParams.set('page', String(page));
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, 20000);
      try {
        const response = await fetch(url.href, { credentials: 'same-origin', signal: controller.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error('section');
        return new DOMParser().parseFromString(await response.text(), 'text/html');
      } finally {
        clearTimeout(timeout);
        signal.removeEventListener('abort', abort);
      }
    }

    parseIndex(document, page, expectedCount) {
      const element = document.querySelector('[data-ace-search-index]');
      if (!element || element.dataset.collectionId !== this.dataset.collectionId || element.dataset.collectionHandle !== this.dataset.collectionHandle) throw new Error('scope');
      const count = Number(element.dataset.count);
      const pages = Math.max(1, Number(element.dataset.pages));
      if (count > MAX_PRODUCTS) throw new Error('limit');
      if (!Number.isInteger(count) || count < 0 || Number(element.dataset.page) !== page || Number(element.dataset.pageSize) !== 250 || pages !== Math.max(1, Math.ceil(count / 250)) || (expectedCount !== undefined && count !== expectedCount)) throw new Error('incomplete');
      const products = Array.from(element.querySelectorAll('[data-product-id]')).map((item) => {
        const skus = JSON.parse(item.dataset.skus);
        const variantCount = Number(item.dataset.variantCount);
        const indexedCount = Number(item.dataset.indexedVariantCount);
        if (!/^\d+$/.test(item.dataset.productId) || !Array.isArray(skus) || !skus.every((sku) => typeof sku === 'string' || sku === null) || !Number.isInteger(variantCount) || variantCount !== indexedCount || indexedCount !== skus.length) throw new Error('incomplete');
        return { id: item.dataset.productId, title: normalize(item.dataset.title), skus: skus.map(normalize) };
      });
      if (products.length !== Math.max(0, Math.min(250, count - (page - 1) * 250))) throw new Error('incomplete');
      return { products, count, pages };
    }

    async loadIndex(scope) {
      if (this.index) return this.index;
      if (this.indexPromise) return this.indexPromise;
      const controller = new AbortController();
      this.indexController = controller;
      const task = (async () => {
        const first = this.parseIndex(await this.fetchSection(scope, INDEX_SECTION, 1, controller.signal), 1);
        if (Number.isFinite(this.nativeCount) && this.nativeCount !== first.count) throw new Error('changed');
        const rest = await mapConcurrent(Array.from({ length: first.pages - 1 }, (_, index) => index + 2), async (page) => this.parseIndex(await this.fetchSection(scope, INDEX_SECTION, page, controller.signal), page, first.count));
        if (controller.signal.aborted) throw abortError();
        const products = [first, ...rest].flatMap((part) => part.products);
        if (products.length !== first.count || new Set(products.map((product) => product.id)).size !== first.count) throw new Error('incomplete');
        this.index = products.map((product, index) => ({ ...product, nativePage: Math.floor(index / this.pageSize) + 1 }));
        return this.index;
      })();
      this.indexPromise = task;
      try {
        return await task;
      } catch (error) {
        controller.abort();
        if (this.indexPromise === task) this.indexPromise = null;
        throw error;
      }
    }

    async search(page = 1, moveFocus = false) {
      const query = this.input.value.trim();
      if (!query) return this.clear();
      if (this.waitingForFacets) return;
      this.cancelResults();
      const generation = this.generation;
      const scope = scopeURL(window.location.href).href;
      if (scope !== this.currentScope) return this.facetsChanging();
      this.resultController = new AbortController();
      const signal = this.resultController.signal;
      this.clearButton.hidden = false;
      this.message(this.dataset.loadingMessage);
      this.setBusy(true);
      try {
        const index = await this.loadIndex(scope);
        if (!this.isCurrent(generation, scope)) return;
        const results = index.filter((product) => matches(product, query));
        const pages = Math.max(1, Math.ceil(results.length / this.pageSize));
        page = Math.min(Math.max(1, page), pages);
        const products = results.slice((page - 1) * this.pageSize, page * this.pageSize);
        const neededPages = [...new Set(products.map((product) => product.nativePage))].filter((number) => !this.cards.has(number));
        await mapConcurrent(neededPages, async (number) => {
          const document = await this.fetchSection(scope, this.dataset.sectionId, number, signal);
          if (!this.isCurrent(generation, scope)) throw abortError();
          const state = document.querySelector('[data-ace-search-state]');
          if (!state || Number(state.dataset.page) !== number || Number(state.dataset.count) !== index.length || state.dataset.collectionId !== this.dataset.collectionId) throw new Error('changed');
          this.cacheCards(document, number);
        });
        if (!this.isCurrent(generation, scope)) return;
        const cards = products.map((product) => this.cards.get(product.nativePage)?.get(product.id));
        if (cards.some((card) => !card)) throw new Error('incomplete');
        this.render(cards, results.length, page, pages, moveFocus);
      } catch (error) {
        if (!this.isCurrent(generation, scope)) return;
        this.restoreOriginal();
        this.resetIndex();
        this.message(error.message === 'limit' ? this.dataset.limitMessage : this.dataset.errorMessage, true);
      } finally {
        if (this.isCurrent(generation, scope)) this.setBusy(false);
      }
    }

    render(cards, count, page, pages, moveFocus) {
      this.restoreOriginal();
      const grid = this.container.querySelector('#product-grid');
      if (!grid || (cards.length && grid.tagName !== 'UL')) throw new Error('grid');
      this.container.querySelectorAll('.pagination-wrapper').forEach((element) => { element.hidden = true; });
      if (grid.tagName === 'UL') grid.innerHTML = cards.join('');
      if (!count) {
        const empty = document.createElement('p');
        empty.className = 'ace-search-empty';
        empty.textContent = this.dataset.emptyMessage;
        if (grid.tagName === 'UL') grid.after(empty);
        else grid.replaceChildren(empty);
      }
      if (pages > 1) {
        const navigation = document.createElement('nav');
        navigation.className = 'ace-search-pagination';
        navigation.setAttribute('aria-label', this.dataset.paginationLabel);
        const addButton = (label, destination, disabled) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = label;
          button.dataset.aceSearchPage = String(destination);
          button.disabled = disabled;
          navigation.append(button);
          return button;
        };
        const previous = addButton(this.dataset.previousLabel, page - 1, page === 1);
        const position = document.createElement('span');
        position.textContent = this.dataset.pageMessage.replace('__PAGE__', String(page)).replace('__PAGES__', String(pages));
        navigation.append(position);
        const next = addButton(this.dataset.nextLabel, page + 1, page === pages);
        grid.after(navigation);
        if (moveFocus) (page === pages ? previous : next).focus();
      }
      this.message(this.dataset.resultsMessage.replace('__COUNT__', String(count)));
    }
  }

  window.AceCollectionSearch = Object.freeze({ normalize, compact, matches, scopeURL, MAX_PRODUCTS });
  if (!customElements.get('ace-collection-search')) customElements.define('ace-collection-search', AceCollectionSearch);
})();
